import { datadogLogs } from '@datadog/browser-logs';

// Canonical IDs per the Snappt observability standard — attach when available.
// asset_review_item_id is non-canonical but useful for this feature.
// HARD RULE (financial data): never log balances, dates, account numbers,
// institution/holder/applicant names — counts and shapes only.
export type VoaSignalContext = {
  applicant_id?: string;
  entry_id?: string;
  company_id?: string;
  property_id?: string;
  asset_review_item_id?: string;
};

export const logPanelStateRestored = (
  ctx: VoaSignalContext,
  detail: {
    source: 'storage' | 'api';
    document_count: number;
    account_count: number;
    // api source only: stored state carries the analyst's edits, so the split
    // between pre-filled and blank documents has no meaning there.
    prefilled_document_count?: number;
    blank_document_count?: number;
    // FVS statements pointing at a document this entry no longer lists.
    orphan_document_count?: number;
  }
): void => {
  datadogLogs.logger.info('voa_panel_state_restored', { ...ctx, ...detail });
};

export const logReviewSubmitFailed = (
  ctx: VoaSignalContext,
  detail: {
    error_type: 'conflict' | 'validation_error' | 'network_error' | 'unknown_error';
    http_status?: number;
    account_count: number;
    statement_count: number;
  }
): void => {
  datadogLogs.logger.warn('voa_review_submit_failed', { ...ctx, ...detail });
};

export const logRecalculateFailed = (
  ctx: VoaSignalContext,
  detail: {
    error_type: 'validation_error' | 'network_error' | 'unknown_error' | 'malformed_response';
    http_status?: number;
  }
): void => {
  datadogLogs.logger.warn('voa_recalculate_failed', { ...ctx, ...detail });
};

export const logReviewLoadFailed = (
  ctx: VoaSignalContext,
  detail: { error_type: 'network_error' | 'unknown_error'; http_status?: number }
): void => {
  datadogLogs.logger.error('voa_review_load_failed', { ...ctx, ...detail });
};

// The wire payload flattens analyst picks into reason_codes, so this log is the
// only record that a rejection was analyst-made rather than FVS-attached. The
// call site debounces to one event per settled selection; the final state per
// statement is the last event sharing its document_id + statement_key.
export const logStatementRejectionChanged = (
  ctx: VoaSignalContext,
  detail: { document_id: string; statement_key: string; codes: number[]; code_count: number }
): void => {
  datadogLogs.logger.info('voa_statement_rejection_changed', { ...ctx, ...detail });
};

// Override strips the derived 2023 and FVS trusts the submitted codes as-is, so
// this is the only record that a stale statement was put back into the total on
// purpose. One event per click; the action is one-way.
export const logStatementStaleStartOverridden = (
  ctx: VoaSignalContext,
  detail: { document_id: string; statement_key: string }
): void => {
  datadogLogs.logger.info('voa_statement_stale_start_overridden', { ...ctx, ...detail });
};

// The entry carries nothing a VOA review can be done on. FVS only runs VOA on asset
// documents, so this means something upstream routed a review that cannot be completed:
// FVS rejects a zero-account complete, and FP has no release action, so the analyst has
// no way to finish it. Counted separately from a load failure so routing gaps are
// visible rather than hiding inside the error state.
export const logNoAssetDocuments = (
  ctx: VoaSignalContext,
  detail: { proof_count: number; asset_proof_count: number }
): void => {
  datadogLogs.logger.warn('voa_review_no_asset_documents', { ...ctx, ...detail });
};

// A VOA_STORAGE_VERSION bump throws away every in-progress session still on the old
// shape, which costs an analyst their unsubmitted work on that review. Logged so the
// real cost of a deploy is countable rather than arriving as a support question.
export const logStoredStateDiscarded = (
  ctx: VoaSignalContext,
  detail: { stored_version?: number; current_version: number }
): void => {
  datadogLogs.logger.warn('voa_stored_state_discarded', { ...ctx, ...detail });
};

export const logStorageUnavailable = (ctx: VoaSignalContext): void => {
  datadogLogs.logger.warn('voa_storage_unavailable', { ...ctx, error_type: 'storage_error' });
};
